import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Icon,
  Typography,
  ButtonBase,
  Drawer,
  useTheme,
  useMediaQuery,
} from '@material-ui/core';

import { convertObjToArr, sortArrByKey } from '../../../utils';
import useStyles from './styles';
import Loading from './Loading';
import Search from '../../../components/Search';
import Module from './Module';
import Lesson from './Lesson';
import Certificate from './Certificate';

const SideMenu = props => {
  const { showSideMenu, handleToggleSideMenu, certificateActive } = props;
  const classes = useStyles();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    list: listCourses,
    error: { list: listCoursesError, current: currentCourseError },
    current,
    loading: { current: currentCourseLoading },
  } = useSelector(state => state.course);
  const [certificateAvailable, setCertificateAvailable] = useState([]);
  const [modulesAndLessons, setModulesAndLessons] = useState([]);
  const [courseMenu, setCourseMenu] = useState(null);
  const [modules, setModules] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [lessonsFiltered, setLessonsFiltered] = useState([]);
  const [hasCourse, setHasCourse] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [filter, setFilter] = useState('');
  const [conclusionPercent, setConclusionPercent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [variantDrawer, setVariantDrawer] = useState('temporary');

  useEffect(() => {
    if (currentCourseLoading) {
      setLoading(true);
    }
  }, [courseMenu, currentCourseLoading]);

  useEffect(() => {
    if (!current) return;
    if (listCoursesError || currentCourseError) return;
    const course = listCourses[current.course];
    if (current.course in listCourses && course.lessons) {
      const {
        modules: courseModules,
        lessons: courseLessons,
        certificate: courseCertificate,
      } = course;

      setCourseMenu(course.id);
      setModules(sortArrByKey(convertObjToArr(courseModules), 'order'));
      setLessons(sortArrByKey(convertObjToArr(courseLessons), 'order'));
      setCertificateAvailable(courseCertificate && !courseCertificate?.locked);
      setConclusionPercent(
        parseInt(listCourses[current.course].conclusionPercent, 10) || 0,
      );
    }
  }, [listCourses, listCoursesError, currentCourseError, current]);

  const filterHandler = useMemo(
    () => ({
      normalizeStr: str => str.normalize('NFD').replace(/[\u0300-\u036f]/g, ''),
      checkFilter: str =>
        str
          ? new RegExp(filterHandler.normalizeStr(filter), 'i').test(
              filterHandler.normalizeStr(str),
            )
          : false,
      applyFilter: arr =>
        arr.filter(item =>
          Object.keys(item).some(key => {
            if (typeof item[key] === 'string') {
              return filterHandler.checkFilter(item[key]);
            }
            return false;
          }),
        ),
    }),
    [filter],
  );

  const filterLessonsByModule = useCallback(
    (lessonsToFilter, moduleId) =>
      lessonsToFilter.filter(lesson => lesson.moduleId === moduleId),
    [],
  );

  useEffect(() => {
    if (
      lessons.length ||
      modules.length ||
      listCoursesError ||
      currentCourseError
    ) {
      setLoading(false);

      if (!listCoursesError && !currentCourseError) {
        setHasCourse(true);
        const lessonsWithoutModule = lessons.filter(l => !l.moduleId);
        const lessonsAndModulesMerged = [...lessonsWithoutModule, ...modules];
        setModulesAndLessons(sortArrByKey(lessonsAndModulesMerged, 'order'));
      }
    }
  }, [lessons, modules, listCoursesError, currentCourseError]);

  useEffect(() => {
    handleToggleSideMenu(!isSmallScreen);
    setVariantDrawer(isSmallScreen ? 'temporary' : 'permanent');
  }, [handleToggleSideMenu, isSmallScreen]);

  useEffect(() => {
    handleToggleSideMenu(!isSmallScreen);
  }, [current, handleToggleSideMenu, isSmallScreen]);

  useEffect(() => {
    if (lessons.length > 0) {
      setLessonsFiltered(filterHandler.applyFilter(lessons));
    }
  }, [filter, filterHandler, lessons]);

  const handleToggleSearch = useCallback(
    (value = !showSearch) => setShowSearch(value),
    [showSearch],
  );
  const handleFilterLessons = useCallback(query => setFilter(query), []);
  const handleModuleStructure = useCallback(
    moduleItem => {
      const lessonsModuleAll = filterLessonsByModule(lessons, moduleItem.id);
      const lessonsModuleFiltered = filterLessonsByModule(
        lessonsFiltered,
        moduleItem.id,
      );
      const filterMatchModule = filterHandler.checkFilter(
        moduleItem.description,
      );
      const filterMatchLessons = lessonsModuleFiltered.length > 0;

      return {
        hidden: !(filterMatchModule || filterMatchLessons),
        collapse: !!(filterMatchModule && !filterMatchLessons),
        lessons: filterMatchModule ? lessonsModuleAll : lessonsModuleFiltered,
      };
    },
    [filterHandler, filterLessonsByModule, lessons, lessonsFiltered],
  );

  return (
    <Drawer
      className={`${classes.container} ${showSideMenu ? 'open' : 'close'}`}
      classes={{ paper: classes.drawerPaper }}
      anchor="right"
      open={showSideMenu}
      onClose={handleToggleSideMenu}
      variant={variantDrawer}
    >
      {loading ? (
        <Loading />
      ) : (
        <>
          {hasCourse && (
            <>
              <Box className="header-bar">
                {!!showSideMenu && (
                  <Search
                    toggleHandler={handleToggleSearch}
                    filterHandler={handleFilterLessons}
                  />
                )}
                {!showSearch && (
                  <ButtonBase
                    className="toggle-side-menu"
                    onClick={handleToggleSideMenu}
                  >
                    <Typography
                      className="toggle-side-menu-text"
                      variant="caption"
                      noWrap
                    >
                      {`${showSideMenu ? 'Esconder' : 'Mostrar'} navegação`}
                    </Typography>
                    <Icon
                      className={`toggle-side-menu-icon ${
                        showSideMenu ? 'opened' : ''
                      }`}
                    >
                      menu_open
                    </Icon>
                  </ButtonBase>
                )}
              </Box>
              {!!showSideMenu && (
                <>
                  <Box className="modules-container">
                    {!!filter && (
                      <Typography variant="overline" className="results-search">
                        Resultados encontrados:
                      </Typography>
                    )}
                    {modulesAndLessons.map(item => {
                      if (
                        item.type === 'lesson' &&
                        filterHandler.checkFilter(item.title)
                      ) {
                        return <Lesson key={item.id} {...item} />;
                      }
                      const moduleStructure = handleModuleStructure(item);
                      return (
                        !moduleStructure.hidden &&
                        !!moduleStructure.lessons.length && (
                          <Module
                            key={item.id}
                            collapse={moduleStructure.collapse}
                            {...item}
                          >
                            {moduleStructure.lessons.map(
                              (lessonModule, i, arr) => (
                                <Lesson
                                  key={lessonModule.id}
                                  {...lessonModule}
                                  first={i === 0}
                                  last={arr.length - 1 === i}
                                />
                              ),
                            )}
                          </Module>
                        )
                      );
                    })}
                    {!!certificateAvailable && (
                      <Certificate isActive={certificateActive} />
                    )}
                  </Box>
                  <Box className="course-progress-container">
                    <Box className="course-progress-bar">
                      <Box
                        className="course-progress-bar-fill"
                        width={`${conclusionPercent}%`}
                      />
                    </Box>
                    <Typography
                      className="course-progress-text"
                      variant="overline"
                    >
                      {`${conclusionPercent}% Concluído`}
                    </Typography>
                  </Box>
                </>
              )}
            </>
          )}
        </>
      )}
    </Drawer>
  );
};

export default memo(SideMenu);
